import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Ticket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function VerificarComparacionPage() {
  const { folio } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/comparisons/verificar/${folio}`);
        if (active) setData(res.data);
      } catch {
        if (active) setData({ valido: false, folio });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [folio]);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="verificar-comparacion-page">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="bg-[#193966] px-6 py-5 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-white" />
          <div>
            <h1 className="text-white text-lg font-semibold leading-tight">Verificación de Trazabilidad</h1>
            <p className="text-blue-200 text-xs">QA Asesores · Informe Ejecutivo / Comparación</p>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center py-10 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mb-3" />
              Verificando folio…
            </div>
          ) : data?.valido ? (
            <div data-testid="verificar-valido">
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-5">
                <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">Documento auténtico</p>
                  <p className="text-sm text-green-700">El folio corresponde a una comparación registrada en el sistema.</p>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <Row label="Folio" value={data.folio} mono />
                <Row label="Cliente" value={data.cliente} />
                <Row label="Documento 1" value={data.documento1} />
                <Row label="Documento 2" value={data.documento2} />
                <Row label="Fecha" value={data.fecha} />
                <Row label="Realizado por" value={data.realizado_por || "-"} />
                <Row label="Total de diferencias" value={`${data.total_diferencias ?? 0}`} />
                <Row label="Eventos registrados" value={`${data.total_eventos ?? 0}`} />
                {data.ticket_numero && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <Ticket className="w-4 h-4 text-amber-600" />
                    <span className="text-slate-700">Ticket Mesa de Ayuda asociado:</span>
                    <span className="font-semibold text-amber-700">#{data.ticket_numero}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-10 text-center" data-testid="verificar-invalido">
              <XCircle className="w-12 h-12 text-red-500 mb-3" />
              <p className="font-semibold text-slate-800">Folio no encontrado</p>
              <p className="text-sm text-slate-500 mt-1">
                El folio <span className="font-mono">{data?.folio || folio}</span> no corresponde a ninguna
                comparación de trazabilidad registrada.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 pb-2">
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-800 font-medium text-right ${mono ? "font-mono" : ""}`}>{value || "-"}</span>
  </div>
);
